import { useEffect, useState } from "react";
import { toast } from "sonner";
import { FileText, Eye, Trash2 } from "lucide-react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export default function AdminCandidates() {
  const [candidates, setCandidates] = useState([]);
  const [detail, setDetail] = useState(null);

  useEffect(() => { api.get("/admin/candidates").then((r) => setCandidates(r.data)).catch(() => {}); }, []);

  const openDetail = async (id) => {
    const { data } = await api.get(`/admin/candidates/${id}`);
    setDetail(data);
  };

  const viewDoc = async (doc) => {
    try {
      const res = await api.get(`/candidate/documents/${doc.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      toast.error("Could not open document");
    }
  };

  const removeCandidate = async () => {
    if (!window.confirm(`Delete ${detail.candidate.name}'s profile? Personal data and documents are removed permanently; applications stay as "Deleted candidate". They must register again to apply.`)) return;
    try {
      await api.delete(`/admin/candidates/${detail.candidate.id}`);
      toast.success("Profile deleted");
      setDetail(null);
      api.get("/admin/candidates").then((r) => setCandidates(r.data));
    } catch { toast.error("Delete failed"); }
  };

  return (
    <div>
      <p className="eyebrow">Talent</p>
      <h1 className="mt-3 font-serif text-3xl tracking-tight">Candidates</h1>
      <p className="mt-2 font-sans text-sm text-[#94A3B8]">Candidate records are kept separate from guest profiles.</p>

      <div className="mt-8 overflow-hidden rounded-lg border border-white/5">
        <table className="w-full text-left">
          <thead className="bg-[#18202E]">
            <tr className="font-mono text-[10px] uppercase tracking-widest text-[#94A3B8]">
              <th className="px-5 py-3">Name</th>
              <th className="px-5 py-3">Email</th>
              <th className="px-5 py-3">Auth</th>
              <th className="px-5 py-3">Applications</th>
            </tr>
          </thead>
          <tbody>
            {candidates.map((c) => (
              <tr key={c.id} onClick={() => openDetail(c.id)} data-testid={`candidate-row-${c.id}`} className="cursor-pointer border-t border-white/5 hover:bg-white/5">
                <td className="px-5 py-4 font-sans text-sm">{c.name}</td>
                <td className="px-5 py-4 font-sans text-sm text-[#94A3B8]">{c.email}</td>
                <td className="px-5 py-4 font-mono text-[10px] uppercase tracking-widest text-[#94A3B8]">{c.auth_provider === "google" ? "Google" : "Email & password"}</td>
                <td className="px-5 py-4 font-serif text-lg text-gold">{c.application_count}</td>
              </tr>
            ))}
            {candidates.length === 0 && <tr><td colSpan={4} className="px-5 py-12 text-center font-sans text-sm text-[#94A3B8]">No candidates yet.</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-2xl border-white/10 bg-[#121722] text-[#F1F5F9]">
          <DialogHeader><DialogTitle className="font-serif text-2xl">{detail?.candidate?.name}</DialogTitle></DialogHeader>
          {detail && (
            <button onClick={removeCandidate} data-testid="candidate-delete-btn" className="flex w-fit items-center gap-1.5 rounded-md border border-rose-500/40 px-3 py-1.5 font-sans text-xs text-rose-300 hover:bg-rose-500/10"><Trash2 size={13} /> Delete profile</button>
          )}
          {detail && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-3 font-sans text-sm">
                <div><p className="text-xs text-[#94A3B8]">Email</p>{detail.candidate.email}</div>
                <div><p className="text-xs text-[#94A3B8]">Phone</p>{detail.candidate.phone || "—"}</div>
                <div><p className="text-xs text-[#94A3B8]">Nationality</p>{detail.candidate.nationality || "—"}</div>
                <div><p className="text-xs text-[#94A3B8]">Location</p>{detail.candidate.current_location || "—"}</div>
              </div>
              <div>
                <p className="eyebrow mb-3">Applications</p>
                <div className="space-y-2">
                  {detail.applications.map((a) => (
                    <div key={a.id} className="flex items-center justify-between rounded-md border border-white/5 bg-[#0A0D14] px-4 py-2.5 font-sans text-sm">
                      <span>{a.job_title}</span><span className="font-mono text-[10px] uppercase tracking-widest text-gold">{a.status}</span>
                    </div>
                  ))}
                  {detail.applications.length === 0 && <p className="font-sans text-sm text-[#94A3B8]">No applications.</p>}
                </div>
              </div>
              <div>
                <p className="eyebrow mb-3">Document Vault (private)</p>
                <div className="space-y-2">
                  {detail.documents.map((d) => (
                    <div key={d.id} className="flex items-center justify-between rounded-md border border-white/5 bg-[#0A0D14] px-4 py-2.5">
                      <span className="flex items-center gap-2 font-sans text-sm"><FileText size={15} className="text-gold" />{d.original_filename} <span className="text-xs text-[#94A3B8]">({d.doc_type})</span></span>
                      <button onClick={() => viewDoc(d)} data-testid={`admin-doc-view-${d.id}`} className="flex items-center gap-1.5 rounded-md border border-white/10 px-3 py-1.5 text-xs text-[#94A3B8] hover:border-gold hover:text-gold"><Eye size={13} /> View</button>
                    </div>
                  ))}
                  {detail.documents.length === 0 && <p className="font-sans text-sm text-[#94A3B8]">No documents uploaded.</p>}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
